import { Col, Row } from 'antd';
import { MasterUseCaseInterface } from 'application/usecases/MasterUseCase';
import { UserStorageUseCaseInterface } from 'application/usecases/UserStorageUseCase';
import { UserUseCaseInterface } from 'application/usecases/UserUseCase';
import chevronRight from 'assets/icons/chevron-right-small.svg';
import { MemberInfo } from 'domain/entities/UserEntity';
import { AlertModalProps, ConfirmModalProps } from 'presentation/components/Modal/Modal.type';
import { openErrorNoti, openSuccessNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import {
    completeFetching,
    showAlert,
    showConfirm,
    startFetching,
} from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { AUTH_ROUTES, PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import getMessage from 'utilities/constants/MESSAGE';
import { InfomationStatus } from './components/InfomationComponent';
import { NoDataForm } from './components/NoDataForm';
import { TabComponent } from './components/TabComponents';
import { Item } from './models/Item';

interface props {
    userUseCase: UserUseCaseInterface;
    masterUseCase: MasterUseCaseInterface;
    userStorageUseCase: UserStorageUseCaseInterface;
}

const MEMBER_TYPE_CODE = '0020000020';
const KYC_STATUS_CODE = '0020000110';
export const MEMBER_TYPE_PERSONAL = 10;
export const MEMBER_TYPE_CORPORATION = 20;

const CU00301Page = ({ userUseCase, masterUseCase, userStorageUseCase }: props) => {
    const [memberInfo, setMemberInfo] = useState<MemberInfo>();
    const [editNothing, setEditNothing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (memberInfo?.unfinancedFlag && !memberInfo.loanConsulExecFlag) {
            const alertProps = {
                message:
                    '登録いただいた情報を確認したところ\n 融資に関してプロに相談が必要になりました。',
                type: 'info',
                okText: 'メッセージ',
                onOk: async () => {
                    try {
                        dispatch(startFetching());
                        const responses = await Promise.all([userUseCase.updateLoanConsul()]);
                        navigate(`${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_012_01}`);
                    } catch (error: any) {
                        openErrorNoti();
                    } finally {
                        dispatch(completeFetching());
                    }
                },
            };
            dispatch(showAlert(alertProps));
        }
    }, [memberInfo]);

    useEffect(() => {
        getMemberType();
    }, []);

    const getMemberType = async () => {
        try {
            dispatch(startFetching());
            const responses = await Promise.all([
                userUseCase.getUserInfo(),
                masterUseCase.getMasterSubCategoryGroupList({
                    categoryCodes: [MEMBER_TYPE_CODE, KYC_STATUS_CODE],
                }),
            ]);
            const userInfo = responses[0].result;
            const masterData = responses[1];
            const memberTypes = masterData[MEMBER_TYPE_CODE];
            const memberType = memberTypes.find(
                (element: any) => element.code == userInfo.memberType,
            );
            if (memberType) {
                userInfo.memberTypeName = memberType.codeNameShort;
            } else {
                userInfo.memberTypeName = '不明';
            }

            // const kycStatus = masterData[KYC_STATUS_CODE];
            const kycStatus = [
                {
                    id: "0020000110",
                    categoryCode: "0020000110",
                    code: "10",
                    codeName: "未確認",
                    codeNameShort: "未確認",
                    displaySeq: 10,
                    defaultFlag: null
                },
                {
                    id: "0020000110",
                    categoryCode: "0020000110",
                    code: "20",
                    codeName: "本人確認NG",
                    codeNameShort: "ブラック",
                    displaySeq: 20,
                    defaultFlag: null
                },
                {
                    id: "0020000110",
                    categoryCode: "0020000110",
                    code: "30",
                    codeName: "本人確認OK",
                    codeNameShort: "ゴールド",
                    displaySeq: 30,
                    defaultFlag: null
                },
                {
                    id: "0020000110",
                    categoryCode: "0020000110",
                    code: "40",
                    codeName: "本人確認OK（プラチナ）",
                    codeNameShort: "プラチナ",
                    displaySeq: 40,
                    defaultFlag: 0
                }
            ];
            
            console.log(kycStatus);

            const kycStatusInfo = kycStatus.find(
                (kyc_obj: any) => kyc_obj.code == userInfo.accountRank,
            );

            if (kycStatusInfo) {
                userInfo.kycResultName = kycStatusInfo.codeNameShort;
            }
            const currentUser = userStorageUseCase.getUserInfo();
            userStorageUseCase.setUser({
                ...userInfo,
                username: currentUser?.user?.username ?? '',
            });
            setMemberInfo(userInfo);
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const getUserInfo = async () => {
        try {
            dispatch(startFetching());
            const response = await userUseCase.getUserInfo();
            const userInfo = response.result;
            const currentUser = userStorageUseCase.getUserInfo();
            userStorageUseCase.setUser({
                ...userInfo,
                username: currentUser?.user?.username ?? '',
            });
            setMemberInfo(userInfo);
            return userInfo;
        } catch (error: any) {
            openErrorNoti();
        }
    };

    const onClickEdit = async () => {
        const userInfo = await getUserInfo();
        if (
            !userInfo ||
            !userInfo.memberType ||
            (userInfo.memberType != MEMBER_TYPE_CORPORATION + '' &&
                userInfo.memberType != MEMBER_TYPE_PERSONAL + '')
        ) {
            //user has not registered
            setEditNothing(true);
            dispatch(completeFetching());
            return;
        }

        if (userInfo.memberType == MEMBER_TYPE_CORPORATION + '' && userInfo.hasRegistInfo) {
            //user has already registered
            navigate(PRIVATE_ROUTES.PATH + '/' + PRIVATE_ROUTES.SUB.CU_007_03);
            dispatch(completeFetching());
            return;
        } else if (userInfo.memberType == MEMBER_TYPE_PERSONAL + '') {
            const STEP_EDIT = [2, 3, 4, 5, 6];
            if (STEP_EDIT.includes(userInfo.step ?? 0)) {
                navigate(PRIVATE_ROUTES.PATH + '/' + PRIVATE_ROUTES.SUB.CU_005_02);
                dispatch(completeFetching());
                return;
            }
        }
        setEditNothing(true);
        dispatch(completeFetching());
    };

    const onClickCreate = async (initInfo?: MemberInfo) => {
        const userInfo = initInfo ?? (await getUserInfo());
        if (userInfo) {
            //user has already registered
            switch (userInfo.step) {
                case 2:
                case 3:
                    navigate(PRIVATE_ROUTES.PATH + '/' + PRIVATE_ROUTES.SUB.CU_003_02);
                    break;
                case 4:
                case 5:
                    navigate(PRIVATE_ROUTES.PATH + '/' + PRIVATE_ROUTES.SUB.CU_005_01);
                    break;
                case 6:
                    navigate(PRIVATE_ROUTES.PATH + '/' + PRIVATE_ROUTES.SUB.CU_003_02);
                    break;
                default:
                    navigate(PRIVATE_ROUTES.PATH + '/' + PRIVATE_ROUTES.SUB.CU_004_00);
                    break;
            }
        }
        dispatch(completeFetching());
    };

    const showPopUpDelete = () => {
        const popupProps: ConfirmModalProps = {
            message: '退会するとアカウントが削除されます。\nよろしいですか？',
            type: 'warning',
            okText: '退会する',
            cancelText: '戻る',
            onOk: () => deleteUser(),
            okButtonProps: {
                style: { background: 'var(--error)' },
            },
        };
        dispatch(showConfirm(popupProps));
    };

    const deleteUser = async () => {
        try {
            dispatch(startFetching());
            const response = await userUseCase.deleteAccount();
            if (response) {
                navigate(`${AUTH_ROUTES.PATH}/${AUTH_ROUTES.SUB.CU_001_00}`);
            }
            openSuccessNoti({ description: getMessage('S0314') });
        } catch (error: any) {
            if (error?.message) {
                const alertProps: AlertModalProps = {
                    message: error?.message,
                    type: 'warning',
                    okText: 'OK',
                };
                dispatch(showAlert(alertProps));
            } else {
                openErrorNoti();
            }
        } finally {
            dispatch(completeFetching());
        }
    };

    const hideCreateMenu = React.useMemo(() => {
        if (memberInfo?.memberType == MEMBER_TYPE_CORPORATION + '') {
            //hide when member type is corporation and already registered data
            return memberInfo?.hasRegistInfo;
        }
        if (memberInfo?.memberType == MEMBER_TYPE_PERSONAL + '') {
            //hide when member type is personal and already registered all data
            return (memberInfo?.step ?? 0) == 6;
        }
        return false;
    }, [memberInfo]);

    const item: Item[] = [
        {
            title: '会員情報の追加登録',
            img: <img src={chevronRight} />,
            onClick: onClickCreate,
            hideMenu: hideCreateMenu,
        },
        {
            title: '会員情報の確認・編集',
            img: <img src={chevronRight} />,
            onClick: onClickEdit,
        },
        {
            title: 'パスワード変更',
            img: <img src={chevronRight} />,
            redirect: PRIVATE_ROUTES.PATH + '/' + PRIVATE_ROUTES.SUB.CU_025_01,
        },
        {
            title: 'MFA認証オプション',
            img: <img src={chevronRight} />,
            redirect: PRIVATE_ROUTES.PATH + '/' + PRIVATE_ROUTES.SUB.CU_054_01,
        },
        {
            title: '退会',
            img: <img src={chevronRight} />,
            redirect: '',
            onClick: showPopUpDelete,
        },
    ];

    return (
        <div className="CU-003-01-container h-full w-full">
            {editNothing ? (
                <PageTitle title="会員情報の確認・編集" />
            ) : (
                <PageTitle title="マイページ" />
            )}
            <section className="h-full w-full">
                <div className="center-item h-auto w-full md:w-full">
                    {editNothing && (
                        <NoDataForm
                            editClick={() => {
                                onClickCreate(memberInfo);
                            }}
                        />
                    )}
                    {!editNothing && (
                        <>
                            <InfomationStatus memberInfo={memberInfo} />
                            <Row className="tabs-container" wrap>
                                {item
                                    .filter((element: Item) => !element.hideMenu)
                                    .map((element: Item, index: number) => (
                                        <Col
                                            key={index}
                                            span={12}
                                            className={
                                                index % 2 == 0
                                                    ? 'pr-0 2xl:pr-[40px]'
                                                    : 'pl-0 2xl:pl-[40px]'
                                            }
                                        >
                                            <TabComponent {...element} />
                                        </Col>
                                    ))}
                            </Row>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};
export default CU00301Page;
